import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mail, Lock, User, AlertCircle, Loader2, Home as HomeIcon } from 'lucide-react';
import { fetchFirebaseConfig } from '@/lib/firebase';

const SignupPage = () => {
  const [form, setForm] = useState({ name: '', email: '', password: '', company_name: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [firebaseEnabled, setFirebaseEnabled] = useState(false);
  const { signup, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchFirebaseConfig().then(cfg => setFirebaseEnabled(!!cfg));
  }, []);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { setError('Please fill in all required fields'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    setError('');
    try {
      await signup(form);
      navigate('/pending-approval');
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSignup = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const result = await loginWithGoogle();
      if (result.pending) {
        navigate('/pending-approval');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Google sign-up failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-airbnb-light-gray to-white px-4" data-testid="signup-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-6">
              <HomeIcon className="w-8 h-8 text-airbnb-pink" />
              <span className="text-2xl font-bold text-airbnb-gray">iHostPMS</span>
            </Link>
            <h1 className="text-2xl font-bold text-airbnb-gray" data-testid="signup-title">Create your account</h1>
            <p className="text-airbnb-medium-gray mt-1">Start managing your properties today</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-airbnb-gray font-medium text-sm mb-1.5 block">Full Name *</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbnb-medium-gray" />
                <Input data-testid="signup-name-input" name="name" value={form.name} onChange={handleChange} className="pl-10 h-11 border-gray-200" placeholder="John Doe" />
              </div>
            </div>
            <div>
              <Label className="text-airbnb-gray font-medium text-sm mb-1.5 block">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbnb-medium-gray" />
                <Input data-testid="signup-email-input" name="email" type="email" value={form.email} onChange={handleChange} className="pl-10 h-11 border-gray-200" placeholder="you@example.com" />
              </div>
            </div>
            <div>
              <Label className="text-airbnb-gray font-medium text-sm mb-1.5 block">Password *</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbnb-medium-gray" />
                <Input data-testid="signup-password-input" name="password" type="password" value={form.password} onChange={handleChange} className="pl-10 h-11 border-gray-200" placeholder="Min 6 characters" />
              </div>
            </div>
            <div>
              <Label className="text-airbnb-gray font-medium text-sm mb-1.5 block">Company Name</Label>
              <Input data-testid="signup-company-input" name="company_name" value={form.company_name} onChange={handleChange} className="h-11 border-gray-200" placeholder="Optional" />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-testid="signup-error">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /><span>{error}</span>
              </div>
            )}

            <Button data-testid="signup-submit-btn" type="submit" disabled={loading} className="w-full h-12 bg-airbnb-pink hover:bg-airbnb-dark-pink text-white font-semibold rounded-xl">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
            </Button>
          </form>

          {firebaseEnabled && (
            <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
                <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-airbnb-medium-gray">or</span></div>
              </div>

              <Button data-testid="google-signup-btn" variant="outline" onClick={handleGoogleSignup} disabled={googleLoading} className="w-full h-12 border-gray-200 hover:bg-gray-50 font-medium text-airbnb-gray rounded-xl">
                {googleLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : (
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                )}
                Sign up with Google
              </Button>
            </>
          )}

          <p className="text-center mt-6 text-sm text-airbnb-medium-gray">
            Already have an account?{' '}
            <Link to="/login" className="text-airbnb-pink font-semibold hover:underline" data-testid="login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
